import { z } from 'zod';

import { CreativeType, JobStatus } from './enums.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KREATİF İŞİNİN AŞAMASI VE İLERLEMESİ (Dalga 5b, 2 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 * Karar defteri (plan §4, 2 Eki): görsel, video, UGC — HER üretim sohbette
 * canlı kart olarak ilerler (aşama + yüzde + kalan süre); geçmişteki kutular
 * da yüzde + aşama gösterir. Kullanıcı "kaçta" diye sormak zorunda kalmaz.
 *
 * Neden tek sayı (`CreativeJob.progress`) yetmedi — ölçülen arıza: hızlı
 * klipte H3 take'i QA'dan döndü, Kling yedeği 6 dk sürdü; sağlayıcının yazdığı
 * yüzde ikinci take bandının ucunda (%76) DURDU ve kullanıcı dört kez "şu an
 * kaçta" diye sordu. Sayı hattın neresinde olunduğunu bilmiyordu, yedek
 * modelin ne kadar süreceğini de.
 *
 * Yeni düzen:
 *   • Worker GERÇEK aşamayı yazar (`stage` + `stageStartedAt` + bu aşamanın
 *     ölçülen süresi `etaSeconds` + `stageDetail`), aşama geçişinde o anki
 *     yüzdeyi `progress`e TABAN olarak yazar (`creativeJobStageTransition`).
 *   • Ekran (sohbet kartı, geçmiş kutusu) yüzdeyi ZAMANLA kendisi ilerletir
 *     (`creativeJobProgress`) — yüzde her yoklamada sunucudan itilmez; canlı
 *     olay (`creatives` konusu) yalnız AŞAMA değişince gider.
 *   • RETRYING barı DONDURMAZ: kalan yolu yeniden paylaştırır ve yedek modelin
 *     kendi süresiyle (Kling ~360 sn) ilerler.
 *
 * 🔴 Bar ASLA geri gitmez: başlangıç `max(taban, aşamanın nominal başı)`,
 * aşama içinde zamanla artan, tavanı %99 (yalnız DONE %100). Garanti,
 * tabanın (`floorPercent`) her aşama geçişinde yazılmasına ve ekranın
 * gösterdiği son değeri SONUCA alt sınır olarak uygulamasına dayanıyor — iki
 * tarafın saati farklı olabilir.
 *
 * 🔴 `floorPercent` YALNIZ sunucunun yazdığı taban (`CreativeJob.progress`).
 * Ekranın gösterdiği son değer TABANA KATILMAZ — sonuca `max` ile uygulanır.
 * Neden (Dalga 5b entegrasyonu, ölçüldü): bant tabandan başlıyor ve aşama içi
 * kesir GEÇEN TOPLAM süreden hesaplanıyor; her tikte yükselen taban bandı da
 * yukarı kaydırıp kesri yeniden ekliyor. Kling yedeğinde (360 sn, taban %64)
 * saniyelik tikle bar 60. saniyede %92'ye, 4. dakikada %98'e koşuyordu — yani
 * bu dalganın düzelttiği "yüksek yüzdede takılı" görüntünün aynısı. Doğrusu:
 * `max(creativeJobProgress({…, floorPercent: progress}).percent, gösterilen)`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Aşama
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prisma `CreativeJobStage` — sıra BİLDİRİM sırası. Hat kesin ileri DEĞİL:
 * RETRYING'den sonra yeniden QUALITY_CHECK gelir (yedek take'in de denetimi).
 * DONE/FAILED terminal; FAILED barın dışında (yüzde son tabanda donar).
 */
export const CreativeJobStage = {
  /// Kuyrukta — kredi rezerve edildi, worker henüz almadı.
  QUEUED: 'QUEUED',
  /// Girdi hazırlığı: fotoğraf ölçme/temiz kopya, prompt, konuşma metni, ses.
  PREPARING: 'PREPARING',
  /// Sağlayıcıda üretim (görsel, take, dudak senkronu).
  GENERATING: 'GENERATING',
  /// Yargıç/QA (görsel denetim, yüz denetimi, take QA'sı).
  QUALITY_CHECK: 'QUALITY_CHECK',
  /// Yedek model ya da yeniden üretim — QA reddi/zayıflığı, sağlayıcı hatası.
  RETRYING: 'RETRYING',
  /// Ses-görüntü birleştirme, müzik, büyütme, kapak karesi.
  POST_PROCESSING: 'POST_PROCESSING',
  /// Depoya yükleme + kayıt + kesinleşme.
  SAVING: 'SAVING',
  DONE: 'DONE',
  FAILED: 'FAILED',
} as const;
export type CreativeJobStage = (typeof CreativeJobStage)[keyof typeof CreativeJobStage];
export const creativeJobStageSchema = z.enum(CreativeJobStage);
export const CREATIVE_JOB_STAGE_ORDER: readonly CreativeJobStage[] =
  Object.values(CreativeJobStage);
export const CREATIVE_JOB_TERMINAL_STAGES: readonly CreativeJobStage[] = [
  CreativeJobStage.DONE,
  CreativeJobStage.FAILED,
];
export function isCreativeJobTerminalStage(stage: string): boolean {
  return (CREATIVE_JOB_TERMINAL_STAGES as readonly string[]).includes(stage);
}

/**
 * Aşaması yazılmamış satır (göçten önceki ya da aşama yazmayan eski yol) için
 * DURUMDAN türetilen aşama. Göç (`20261002170000_kreatif_is_asamasi`) eski
 * satırları AYNI eşlemeyle doldurdu. CANCELED → FAILED: hat çıktısız bitti;
 * sebebi ekran `status`tan okur.
 */
export function creativeJobStageForStatus(status: JobStatus | string): CreativeJobStage {
  switch (status) {
    case JobStatus.SUCCEEDED:
      return CreativeJobStage.DONE;
    case JobStatus.FAILED:
    case JobStatus.CANCELED:
      return CreativeJobStage.FAILED;
    case JobStatus.RUNNING:
      return CreativeJobStage.GENERATING;
    default:
      return CreativeJobStage.QUEUED;
  }
}

/**
 * Satırın GEÇERLİ aşaması — REST izdüşümü, sohbet kartı ve ajan AYNI kuralı
 * okusun diye ortakta (Dalga 5b entegrasyonu; önce ikisi ayrı yazılmıştı ve
 * yarış dallarında farklı aşama söylüyorlardı).
 *   • Terminal DURUM aşamayı ezer: iş bitti/düştü ama aşama yazılamadıysa da
 *     "hazır"/"durdu" (aşama ve durum aynı işlemde yazılıyor; bu yalnız emniyet).
 *   • Aşama terminal ama iş hâlâ açık (yarış) ya da hiç yazılmamış → durumdan.
 *   • RUNNING + varsayılan QUEUED = aşama yazmayan yol → GENERATING.
 */
export function creativeJobEffectiveStage(
  status: JobStatus | string,
  stage: string | null | undefined,
): CreativeJobStage {
  const normalized = String(status ?? '').toUpperCase();
  const fromStatus = creativeJobStageForStatus(normalized);
  if (isCreativeJobTerminalStage(fromStatus)) return fromStatus;
  const stored =
    stage && (CREATIVE_JOB_STAGE_ORDER as readonly string[]).includes(stage)
      ? (stage as CreativeJobStage)
      : null;
  if (!stored || isCreativeJobTerminalStage(stored)) return fromStatus;
  if (stored === CreativeJobStage.QUEUED && normalized === JobStatus.RUNNING) {
    return CreativeJobStage.GENERATING;
  }
  return stored;
}

// ═══════════════════════════════════════════════════════════════════════════
// Aşama ayrıntısı (`CreativeJob.stageDetail`)
// ═══════════════════════════════════════════════════════════════════════════

/// RETRYING'in sebebi — ekran etiketi buna göre ("kalite kontrolünden geçmedi").
export const CREATIVE_JOB_RETRY_REASONS = [
  'qa_rejected',
  'qa_weak',
  'provider_error',
  'timeout',
] as const;
export type CreativeJobRetryReason = (typeof CREATIVE_JOB_RETRY_REASONS)[number];

/// POST_PROCESSING'in o anki adımı.
export const CREATIVE_JOB_POST_STEPS = ['music', 'mux', 'upscale', 'poster'] as const;
export type CreativeJobPostStep = (typeof CREATIVE_JOB_POST_STEPS)[number];

/// `stageDetail.extra`nın tavanı (sn) — bir işin planlı ek üretimi bundan uzun sürmez.
export const CREATIVE_JOB_EXTRA_SECONDS_MAX = 3600;

/**
 * `{model?, attempt?, fallback?, reason?, step?, extra?}`.
 *
 * `model` İÇ kısa ad (`h3`, `kling`, `gpt-image`) — süre tahmini ve teşhis
 * için; 🔴 ekranda ADI gösterilmez (kullanıcı "yedek model" görür). Uç
 * kimliği/fiyat buraya yazılmaz. `attempt` 1'den sayar; `fallback` = bu
 * deneme ilkinden FARKLI modelle.
 *
 * `extra` (sn) — bu aşamadan SONRA türün tablo yolunda OLMAYAN ama bu işte
 * planlı ikinci bir üretim adımı. Neden (Dalga 5b denetimi, ölçüldü): UGC'de
 * sunucu karesi (gpt-image) ile dudak senkronu, denemede manken karesi ile
 * giydirme ART ARDA iki GENERATING; birincinin kalanına tablo yalnız denetim/
 * son işlem/kayıt ekliyordu ve ikinci adım başlayınca kalan süre YÜKSELİYORDU
 * (UGC'de ~2 dk → ~5 dk, bar %38'de düz). Worker ikinci adımın süresini
 * burada bildirir; ekran kalanı ve bandı onunla kurar.
 */
export const creativeJobStageDetailSchema = z.object({
  model: z
    .string()
    .regex(/^[a-z0-9][a-z0-9._-]{0,39}$/)
    .optional(),
  attempt: z.number().int().min(1).max(20).optional(),
  fallback: z.boolean().optional(),
  reason: z.enum(CREATIVE_JOB_RETRY_REASONS).optional(),
  step: z.enum(CREATIVE_JOB_POST_STEPS).optional(),
  extra: z.number().int().min(0).max(CREATIVE_JOB_EXTRA_SECONDS_MAX).optional(),
});
export type CreativeJobStageDetail = z.infer<typeof creativeJobStageDetailSchema>;

/**
 * DB'deki `Json?` → ayrıntı. Alan alan ve HOŞGÖRÜLÜ: tanınmayan tek bir alan
 * (worker'ın yeni bir sebebi) bütün ayrıntıyı düşürmesin — o alan atlanır.
 * Boş ya da nesne değilse `null`.
 */
export function parseCreativeJobStageDetail(raw: unknown): CreativeJobStageDetail | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const shape = creativeJobStageDetailSchema.shape;
  const out: CreativeJobStageDetail = {};
  const model = shape.model.safeParse(source.model);
  if (model.success && model.data !== undefined) out.model = model.data;
  const attempt = shape.attempt.safeParse(source.attempt);
  if (attempt.success && attempt.data !== undefined) out.attempt = attempt.data;
  const fallback = shape.fallback.safeParse(source.fallback);
  if (fallback.success && fallback.data !== undefined) out.fallback = fallback.data;
  const reason = shape.reason.safeParse(source.reason);
  if (reason.success && reason.data !== undefined) out.reason = reason.data;
  const step = shape.step.safeParse(source.step);
  if (step.success && step.data !== undefined) out.step = step.data;
  const extra = shape.extra.safeParse(source.extra);
  if (extra.success && extra.data !== undefined && extra.data > 0) out.extra = extra.data;
  return Object.keys(out).length > 0 ? out : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Aşama süreleri — türe göre (barın paylaştırma ağırlığı + kalan süre)
// ═══════════════════════════════════════════════════════════════════════════

/// Aşama → beklenen saniye. Eksik ya da 0 = bu türde o aşamaya girilmez.
export type CreativeJobStageSeconds = Readonly<Partial<Record<CreativeJobStage, number>>>;

/// Aşamanın ne süresi verildi ne tablo değeri var — tavan değil, nötr bir tahmin.
const DEFAULT_STAGE_SECONDS = 30;

/**
 * Ölçülen ortalamalar (sn). Hem barın aşamalara paylaştırılması hem de kalan
 * sürenin toplanması bunlardan. Worker bir aşamaya girerken o aşamanın KENDİ
 * süresini bildiğinde (`etaSeconds` — ör. Kling yedeği ~360) tablo değeri
 * yalnız o aşama için ezilir.
 *
 * Kaynaklar:
 *   AD_IMAGE   — DB, son 60 gün 55 başarılı iş: koşu ort. 129 sn (p50 130),
 *                kuyruk ort. 26 sn. Aşama kırılımı tahmin (gpt-image ~36–43
 *                sn/kare, bake-off; yargıç ~30 sn).
 *   PRODUCT_VIDEO (hızlı klip) — ücretli deneme 2 Eki (`kreatif-olcum/
 *                ornekler/d4-s6`): medya hazırlığı ~15 sn, 8 sn 1080P H3 take
 *                16–22 sn, QA (medya + konumlayıcı + yargıç) 65–68 sn, EL
 *                müzik 5–10 sn; iki take'li iş 177 sn. RETRYING 120 = model
 *                bilinmezken orta değer (H3 yeniden take ~25, Kling ~360 —
 *                worker `etaSeconds` ile modelinkini yazar).
 *   UGC_VIDEO  — ÖLÇÜLMEDİ (tek başarılı koşu, $1,63): API tahmini
 *                `60 + 12×süre + 35` (10 sn → ~215) aşamalara bölündü.
 *   IMPROVED / RESIZED — görsel hattı; ayrıca ölçülmedi.
 *
 * 🔴 Tablo türün GERÇEK hattını anlatır: girilmeyen aşama YAZILMAZ (0 = yok).
 * Görsel hattında (fal.provider, fal-studio: banner/deneme/büyütme) kalite
 * denetimi, model yeniden denemesi ve son işlem YOK — yalnız kuyruk →
 * hazırlık → üretim → kayıt. Önceden QC 35 + RETRY 70 (⅓) + POST 10 sayılıyor,
 * her görselin kalanına ~73 sn hayalî süre ekleniyor ve barın ~%40'ı hiç
 * yaşanmayacak aşamalara ayrılıyordu (Dalga 5b denetimi: üretim başında
 * "~3 dk", gerçeği ~70 sn; şeritte hiç yapılmamış "Kontrol ✓"). Geçici hatayla
 * kuyruğa dönüş (RETRYING `provider_error`/`timeout`) tablodan değil sebepten
 * yürür (`remainingAfter`).
 *
 * GENERATING 109 = ölçülen koşu ortalaması 129 − hazırlık 15 − kayıt 5: metinden
 * üretim (~65) ile düzenleme ucu (defter 107,6) karışık. Önceki toplam ölçüme
 * hayalî aşamalarla tutuyordu; şimdi aynı toplam gerçek aşamada. Bu değer
 * yalnız üretim BAŞLAMADAN (kuyruk/hazırlık bandı, kuyruk tahmini) okunur —
 * aşama başlarken worker ucun kendi medyanını yazar (`etaSeconds`).
 */
const IMAGE_STAGE_SECONDS: CreativeJobStageSeconds = {
  QUEUED: 20,
  PREPARING: 15,
  GENERATING: 109,
  SAVING: 5,
};

/**
 * ÖZEL MANKEN (`StudioModel`, `studio_model_create` kartı) — kreatif tipi
 * değil, kendi satırı; tek üretim aşaması (Seedream 1536×2048, ~60 sn tahmin,
 * ölçülmedi). Görselin tablosuyla çizilseydi kalan süre hayalî aşamalarla
 * şişerdi (Dalga 5b denetimi: 60 sn'lik iş için "~3 dk").
 */
export const STUDIO_MODEL_PROGRESS_TYPE = 'STUDIO_MODEL';
export const STUDIO_MODEL_STAGE_SECONDS: CreativeJobStageSeconds = {
  QUEUED: 10,
  GENERATING: 60,
};

export const CREATIVE_JOB_STAGE_SECONDS: Readonly<
  Partial<Record<CreativeType, CreativeJobStageSeconds>>
> = {
  AD_IMAGE: IMAGE_STAGE_SECONDS,
  IMPROVED: IMAGE_STAGE_SECONDS,
  RESIZED: IMAGE_STAGE_SECONDS,
  PRODUCT_VIDEO: {
    QUEUED: 5,
    PREPARING: 15,
    GENERATING: 25,
    QUALITY_CHECK: 66,
    RETRYING: 120,
    POST_PROCESSING: 15,
    SAVING: 5,
  },
  UGC_VIDEO: {
    QUEUED: 5,
    PREPARING: 45,
    GENERATING: 150,
    QUALITY_CHECK: 30,
    RETRYING: 150,
    POST_PROCESSING: 20,
    SAVING: 5,
  },
};

/// Türün aşama süreleri; tabloda olmayan tür (render kuyruğundan geçmeyen) görselinkine düşer.
export function creativeJobStageWeights(type: CreativeType | string): CreativeJobStageSeconds {
  if (type === STUDIO_MODEL_PROGRESS_TYPE) return STUDIO_MODEL_STAGE_SECONDS;
  return CREATIVE_JOB_STAGE_SECONDS[type as CreativeType] ?? IMAGE_STAGE_SECONDS;
}

/// Tür bu aşamaya hiç giriyor mu (tablo süresi > 0) — ekranın aşama şeridi girilmeyen adımı göstermez.
export function creativeJobStageUsed(
  weights: CreativeJobStageSeconds,
  stage: CreativeJobStage,
): boolean {
  const seconds = weights[stage];
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// Etiket anahtarları (ekran çevirir — sunucu metin göndermez)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Aşama etiketinin mesaj anahtarları: `<CREATIVE_JOB_STAGE_MESSAGES>.label.<anahtar>`
 * (`apps/web/messages/{en,tr}.json`; kalan 11 dil betikten, yoksa İngilizce).
 * Gecikme cümlesi `<CREATIVE_JOB_STAGE_MESSAGES>.overdue`. Anahtar kümesi ile
 * mesajların eşitliğini API `creative/creative-progress.spec.ts` zorluyor.
 */
export const CREATIVE_JOB_STAGE_MESSAGES = 'dashboard.creatives.jobStage';
export const CREATIVE_JOB_STAGE_LABEL_KEYS = [
  'queued',
  'preparing',
  'generating',
  'quality_check',
  'retrying',
  'retrying_quality',
  'retrying_weak',
  'retrying_fallback',
  'retrying_provider',
  'post_processing',
  'post_music',
  'post_mux',
  'post_upscale',
  'post_poster',
  'saving',
  'done',
  'failed',
] as const;
export type CreativeJobStageLabelKey = (typeof CREATIVE_JOB_STAGE_LABEL_KEYS)[number];

const STAGE_LABEL: Readonly<Record<CreativeJobStage, CreativeJobStageLabelKey>> = {
  QUEUED: 'queued',
  PREPARING: 'preparing',
  GENERATING: 'generating',
  QUALITY_CHECK: 'quality_check',
  RETRYING: 'retrying',
  POST_PROCESSING: 'post_processing',
  SAVING: 'saving',
  DONE: 'done',
  FAILED: 'failed',
};

/**
 * Aşama (+ ayrıntı) → etiket anahtarı. Yedek model, sebepten ÖNCE gelir:
 * kullanıcı için asıl haber "başka bir modelle deneniyor" (süre de onunla
 * uzuyor); QA reddi ile sağlayıcı hatasının ayrımı yalnız aynı modelde.
 */
export function creativeJobStageLabelKey(
  stage: CreativeJobStage,
  detail?: CreativeJobStageDetail | null,
): CreativeJobStageLabelKey {
  if (stage === CreativeJobStage.RETRYING) {
    if (detail?.fallback) return 'retrying_fallback';
    if (detail?.reason === 'qa_rejected') return 'retrying_quality';
    /* Zayıf GEÇİŞ ayrı cümle: take denetimden geçti, yalnız kıl payı — "kalite
       kontrolünden geçmedi" demek yanlış olurdu (Dalga 5b denetimi). */
    if (detail?.reason === 'qa_weak') return 'retrying_weak';
    if (detail?.reason === 'provider_error' || detail?.reason === 'timeout') {
      return 'retrying_provider';
    }
    return 'retrying';
  }
  if (stage === CreativeJobStage.POST_PROCESSING && detail?.step) return `post_${detail.step}`;
  return STAGE_LABEL[stage] ?? 'generating';
}

// ═══════════════════════════════════════════════════════════════════════════
// İlerleme (SAF)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Barın nominal yolu. RETRYING içinde ama BEKLENEN değeriyle
 * (`süre × CREATIVE_JOB_RETRY_LIKELIHOOD`): yeniden deneme olursa ona barda
 * yer kalmış olur (yoksa %80'den sonraki dar şeride sıkışırdı), olmazsa bar
 * denetimden sonra o dilimi atlayıp ileri sıçrar — ileri sıçrama dürüst,
 * geri gitme değil. DONE/FAILED bar dışı.
 */
const NOMINAL_PATH: readonly CreativeJobStage[] = [
  CreativeJobStage.QUEUED,
  CreativeJobStage.PREPARING,
  CreativeJobStage.GENERATING,
  CreativeJobStage.QUALITY_CHECK,
  CreativeJobStage.RETRYING,
  CreativeJobStage.POST_PROCESSING,
  CreativeJobStage.SAVING,
];
/// Yeniden deneme SÜRERKEN kalan yol: yedek take'in denetimi KESİN var.
const RETRY_PATH: readonly CreativeJobStage[] = [
  CreativeJobStage.RETRYING,
  CreativeJobStage.QUALITY_CHECK,
  CreativeJobStage.POST_PROCESSING,
  CreativeJobStage.SAVING,
];

/**
 * İşi BAŞTAN koşturan yeniden deneme sebepleri — `render.service` `fail()`
 * geçici sağlayıcı hatasında/süre dolumunda işi kuyruğa döndürür ve sıradaki
 * deneme hazırlıktan başlar (çok adımlı zincirler devralınmaz). Bu RETRYING
 * denetimden SONRAKİ take yeniden denemesi DEĞİL: hattın herhangi bir
 * yerinde yazılabilir, kalanı bütün yoldur.
 */
const RESTART_REASONS: readonly CreativeJobRetryReason[] = ['provider_error', 'timeout'];

/**
 * Kuyruğa dönen işin RETRYING'de beklediği süre (sn) — BullMQ geri çekilmesi
 * (API varsayılanı üstel, taban 2 sn: 2·4·8·16) + kuyrukta yeniden sıra.
 * Ölçülmedi; worker yazmazsa bu.
 */
const REQUEUE_WAIT_SECONDS = 15;

function isRestart(stage: CreativeJobStage, detail: CreativeJobStageDetail | null | undefined) {
  return (
    stage === CreativeJobStage.RETRYING &&
    detail?.reason !== undefined &&
    RESTART_REASONS.includes(detail.reason)
  );
}

/**
 * Yeniden denemenin olasılığı — nominal yolda RETRYING'in ağırlığı. Hızlı
 * klip fiyatındaki yeniden deneme payıyla aynı (`QUICK_CLIP_PRICING.
 * retryShare` = ⅓, production-pricing); görselde ayrıca ölçülmedi.
 */
export const CREATIVE_JOB_RETRY_LIKELIHOOD = 1 / 3;

/// DONE dışında barın tavanı — "%100 ama bitmedi" görülmesin.
const BAR_CEILING = 99;
/// Aşama kendi süresinin sonuna kadar bandının bu kadarını doğrusal yürür…
const LINEAR_SHARE = 0.9;
/// …süre aşılırsa kalan payı asimptotik sürünerek yer; bandın ucuna DEĞMEZ
/// (sonraki aşamanın başı her zaman önünde kalsın).
const IN_STAGE_CEILING = 0.99;

export interface CreativeJobProgressInput {
  stage: CreativeJobStage;
  /// Aşamaya girilen an (`CreativeJob.stageStartedAt`). `null` = süre işlemiyor.
  stageStartedAt: Date | string | null;
  /**
   * BU AŞAMANIN beklenen süresi (sn) — `CreativeJob.etaSeconds`. İşin kalanı
   * DEĞİL. `null` → `stageWeights[stage]`.
   */
  etaSeconds: number | null;
  /// Türün aşama süreleri (`creativeJobStageWeights(type)`).
  stageWeights: CreativeJobStageSeconds;
  /**
   * TABAN: bar bunun altına inmez. HER YERDE `CreativeJob.progress` (aşama
   * geçişinde yazılan yüzde). 🔴 Ekranın gösterdiği son değeri buraya KATMA —
   * bileşik büyür (dosya başlığı); onu sonuca alt sınır olarak uygula.
   */
  floorPercent?: number | null;
  /// Etiketin inceliği (yedek model, sebep, son işlem adımı).
  stageDetail?: CreativeJobStageDetail | null;
}

export interface CreativeJobProgress {
  /// 0–100 tam sayı; DONE dışında en fazla 99.
  percent: number;
  /**
   * İŞİN kalan süresi (sn): bu aşamanın kalanı + sonraki aşamaların tablo
   * süreleri. `null` = bilinmiyor — aşama kendi süresini aştı (`overdue`) ya
   * da iş düştü. Aşılmışken sayı UYDURULMAZ; ekran "biraz uzun sürüyor" der.
   */
  etaSeconds: number | null;
  labelKey: CreativeJobStageLabelKey;
  /// Aşama beklenen süresini aştı.
  overdue: boolean;
}

function positive(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

function clampPercent(value: number | null | undefined): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return Math.max(0, Math.min(100, n));
}

/// Nominal yoldaki ağırlık: RETRYING beklenen değeriyle, diğerleri kendi süresiyle.
function nominalWeight(stage: CreativeJobStage, weights: CreativeJobStageSeconds): number {
  const seconds = positive(weights[stage]);
  return stage === CreativeJobStage.RETRYING ? seconds * CREATIVE_JOB_RETRY_LIKELIHOOD : seconds;
}

/**
 * Bu aşamadan SONRA kalan sürenin toplamı:
 *   • RETRYING, sebep işi baştan koşturuyorsa (`provider_error`/`timeout` —
 *     kuyruğa dönüş): hazırlıktan itibaren BÜTÜN nominal yol.
 *   • diğer RETRYING (denetimden sonraki take): kesin yol — denetim + son
 *     işlem + kayıt.
 *   • başka aşamada nominal yolun beklenen değeri.
 * Hepsine aşamanın bildirdiği planlı ek üretim (`detail.extra`) eklenir.
 */
function remainingAfter(
  stage: CreativeJobStage,
  weights: CreativeJobStageSeconds,
  detail: CreativeJobStageDetail | null | undefined,
): number {
  const extra = positive(detail?.extra);
  if (isRestart(stage, detail)) {
    const from = NOMINAL_PATH.indexOf(CreativeJobStage.PREPARING);
    return (
      NOMINAL_PATH.slice(from).reduce((sum, item) => sum + nominalWeight(item, weights), 0) + extra
    );
  }
  if (stage === CreativeJobStage.RETRYING) {
    return RETRY_PATH.slice(1).reduce((sum, item) => sum + positive(weights[item]), 0) + extra;
  }
  const index = NOMINAL_PATH.indexOf(stage);
  if (index < 0) return extra;
  return (
    NOMINAL_PATH.slice(index + 1).reduce((sum, item) => sum + nominalWeight(item, weights), 0) +
    extra
  );
}

/// Aşamanın nominal yoldaki başlangıç yüzdesi.
function nominalStart(stage: CreativeJobStage, weights: CreativeJobStageSeconds): number {
  const total = NOMINAL_PATH.reduce((sum, item) => sum + nominalWeight(item, weights), 0);
  const index = NOMINAL_PATH.indexOf(stage);
  if (total <= 0 || index <= 0) return 0;
  const before = NOMINAL_PATH.slice(0, index).reduce(
    (sum, item) => sum + nominalWeight(item, weights),
    0,
  );
  return (100 * before) / total;
}

/// Aşama içinde geçen sürenin bandın kesrine çevrilmesi — zamanla kesin artan.
function inStageFraction(elapsedSeconds: number, stageSeconds: number): number {
  if (elapsedSeconds <= 0) return 0;
  const t = elapsedSeconds / stageSeconds;
  if (t <= 1) return LINEAR_SHARE * t;
  return LINEAR_SHARE + (IN_STAGE_CEILING - LINEAR_SHARE) * (1 - Math.exp(-(t - 1)));
}

function startedMs(value: Date | string | null): number | null {
  if (value === null) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Aşamadan yüzde, kalan süre ve etiket (SAF).
 *
 * Band: başlangıç `max(taban, nominal baş)`; aşamanın payı KALAN yolun
 * süreleri içindeki payı (`kendi süresi ÷ (kendi + sonrakiler)`), bitiş
 * `baş + (100 − baş) × pay`. RETRYING'de kalan yol yedek take + denetim +
 * son işlem + kayıt — bar donmaz, kalan şeridi yedek modelin süresiyle
 * (Kling ~360 sn) yürür; kalan süre de onunla yeniden hesaplanır.
 *
 * 🔴 RETRYING'in bandı TABANDAN başlar, nominal başından değil. RETRYING
 * yalnız denetimden sonra yazılmıyor: geçici hatayla kuyruğa dönüş her
 * aşamadan, UGC sunucu karesinin yeniden üretimi dudak senkronundan ÖNCE
 * yazılıyor. Nominal baş (denetimin sonu, %65–78) kullanılınca bar 40–50
 * puan İLERİ sıçrıyor ve bütün yeniden koşu tepedeki dar şeride sıkışıyordu —
 * bu dalganın düzelttiği "yüksek yüzdede takılı" görüntünün aynısı (Dalga 5b
 * denetimi, ölçüldü). Denetimden sonraki take yeniden denemesinde taban
 * zaten nominal başa eşit; davranış değişmiyor.
 *
 * Yeniden koşu (`provider_error`/`timeout`) tabanı GERİ ÇEKMİYOR (bar asla
 * geri gitmez): kalan yol bütün hat olduğu için bant tabandan 99'a kadar
 * süreyle orantılı paylaştırılıyor — bar ile kalan süre birbirini tutuyor.
 *
 * Monotonluk: aynı aşamada zamanla artan; taban yükselirse de artan (bitiş
 * tabanla birlikte kayar); aşama geçişinde yeni baş ≥ yazılan taban.
 */
export function creativeJobProgress(
  input: CreativeJobProgressInput,
  now: Date = new Date(),
): CreativeJobProgress {
  const floor = clampPercent(input.floorPercent);
  const labelKey = creativeJobStageLabelKey(input.stage, input.stageDetail);

  if (input.stage === CreativeJobStage.DONE) {
    return { percent: 100, etaSeconds: 0, labelKey, overdue: false };
  }
  if (input.stage === CreativeJobStage.FAILED) {
    return {
      percent: Math.min(BAR_CEILING, Math.floor(floor)),
      etaSeconds: null,
      labelKey,
      overdue: false,
    };
  }

  const weights = input.stageWeights;
  const restart = isRestart(input.stage, input.stageDetail);
  /* Kuyruğa dönüşün kendi süresi geri çekilme + sıra; tablodaki RETRYING
     değeri (yedek modelin take'i) onu anlatmıyor. */
  const own =
    positive(input.etaSeconds) ||
    (restart ? REQUEUE_WAIT_SECONDS : positive(weights[input.stage])) ||
    DEFAULT_STAGE_SECONDS;
  const after = remainingAfter(input.stage, weights, input.stageDetail);

  const nominal =
    input.stage === CreativeJobStage.RETRYING ? 0 : nominalStart(input.stage, weights);
  const start = Math.min(BAR_CEILING, Math.max(floor, nominal));
  const end = start + (100 - start) * (own / (own + after));

  const started = startedMs(input.stageStartedAt);
  const elapsed = started === null ? 0 : Math.max(0, (now.getTime() - started) / 1000);
  const raw = start + (end - start) * inStageFraction(elapsed, own);
  const percent = Math.min(BAR_CEILING, Math.max(Math.floor(floor), Math.floor(raw)));

  const overdue = elapsed > own;
  return {
    percent,
    etaSeconds: overdue ? null : Math.ceil(own - elapsed + after),
    labelKey,
    overdue,
  };
}

/**
 * AŞAMA GEÇİŞİ YAZIMI (SAF) — worker'ın `creativeJob.updateMany` `data`sı.
 *
 * Geçiş anındaki yüzde (`creativeJobProgress(şimdiki, now)`) yeni `progress`
 * TABANI olur; böylece RETRYING'den QUALITY_CHECK'e dönüşte (nominal yolda
 * daha GERİDE bir aşama) bar geri gitmez. Yazım ayrıca `status: RUNNING`
 * kapılı olmalı (render.service durum kapıları).
 */
export function creativeJobStageTransition(
  current: {
    stage: CreativeJobStage;
    stageStartedAt: Date | string | null;
    etaSeconds: number | null;
    progress: number;
    /**
     * Şimdiki aşamanın ayrıntısı — geçiş anındaki yüzde ekranın gösterdiğiyle
     * AYNI hesaptan çıksın diye (sebep ve `extra` bandı değiştiriyor). Önce
     * verilmiyordu: kuyruğa dönüşte ve planlı ek üretimde geçiş tabanı ekranın
     * gösterdiğinden 8–12 puan yüksek yazılıyor, bar sıçrıyordu (ölçüldü).
     */
    stageDetail?: CreativeJobStageDetail | null;
  },
  next: {
    stage: CreativeJobStage;
    etaSeconds?: number | null;
    detail?: CreativeJobStageDetail | null;
  },
  stageWeights: CreativeJobStageSeconds,
  now: Date = new Date(),
): {
  stage: CreativeJobStage;
  stageStartedAt: Date;
  etaSeconds: number | null;
  stageDetail: CreativeJobStageDetail | null;
  progress: number;
} {
  const reached = creativeJobProgress(
    {
      stage: current.stage,
      stageStartedAt: current.stageStartedAt,
      etaSeconds: current.etaSeconds,
      stageWeights,
      floorPercent: current.progress,
      stageDetail: current.stageDetail ?? null,
    },
    now,
  ).percent;
  const eta = positive(next.etaSeconds);
  return {
    stage: next.stage,
    stageStartedAt: now,
    etaSeconds: eta > 0 ? Math.round(eta) : null,
    stageDetail: next.detail ?? null,
    progress: next.stage === CreativeJobStage.DONE ? 100 : Math.min(BAR_CEILING, reached),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Canlı katman referansı (`LIVE_TOPICS` → `creatives`)
// ═══════════════════════════════════════════════════════════════════════════

export type CreativeLiveRefKind = 'creative' | 'job';

/// `creative:<id>` (kreatif satırı değişti) · `job:<id>` (iş aşaması değişti).
export function creativeLiveRef(kind: CreativeLiveRefKind, id: string): string {
  return `${kind}:${id}`;
}

/** `creativeLiveRef`in tersi — tanınmayan biçimde `null`. */
export function parseCreativeLiveRef(
  ref: string | null | undefined,
): { kind: CreativeLiveRefKind; id: string } | null {
  const match = /^(creative|job):([^:]+)$/.exec(ref ?? '');
  return match ? { kind: match[1] as CreativeLiveRefKind, id: match[2]! } : null;
}
